/** Items and services used on invoice and bill lines. */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount } from '../../lib/money';
import { useAccounts, useAtcs, useItems, useTaxCodes, type Item } from '../../lib/queries';
import { AccountSelect } from '../../components/AccountSelect';
import { Alert, Button, Card, EmptyState, Field, Modal, Money, PageHeader, Skeleton, TableWrap, Td, Th, inputClass } from '../../components/ui';

export function ItemsPage(): JSX.Element {
  const { can } = useAuth();
  const items = useItems();
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const manage = can('sales.manage', 'bills.manage', 'coa.manage');
  return (
    <>
      <PageHeader
        title="Items & Services"
        description="Set the default price, account and tax once; every invoice and bill line picks them up."
        actions={manage && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> New item</Button>}
      />
      <Card>
        {items.isLoading && <Skeleton className="h-40 w-full" />}
        {items.data?.length === 0 && <EmptyState title="No items yet" description="Add the services and goods you sell or buy most." />}
        {items.data && items.data.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th align="right">Sales price</Th>
                <Th align="right">Cost</Th>
                <Th>Tax</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.data.map((i) => (
                <tr key={i.id} className={i.active ? '' : 'opacity-50'}>
                  <Td className="whitespace-nowrap">{i.code}</Td>
                  <Td className="font-medium">{i.name}</Td>
                  <Td>{i.type === 'SERVICE' ? 'Service' : i.type === 'INVENTORY' ? 'Inventory' : 'Non-inventory'}</Td>
                  <Td align="right"><Money>{amount(i.salesPrice)}</Money> <span className="text-xs text-slate-500">/{i.uom}</span></Td>
                  <Td align="right"><Money>{amount(i.purchasePrice)}</Money></Td>
                  <Td>{i.salesTaxCode ?? '—'}</Td>
                  <Td align="right">
                    {manage && (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${i.name}`} onClick={() => setEditing(i)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {editing && <ItemDialog item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function ItemDialog({ item, onClose }: { item: Item | null; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const atcs = useAtcs();
  const [form, setForm] = useState({
    code: item?.code ?? '',
    name: item?.name ?? '',
    type: (item?.type ?? 'SERVICE') as string,
    barcode: (item as (Item & { barcode?: string | null }) | undefined)?.barcode ?? '',
    reorderPoint: (item as (Item & { reorderPoint?: string | null }) | undefined)?.reorderPoint ? String(Number((item as Item & { reorderPoint?: string | null }).reorderPoint)) : '',
    uom: item?.uom ?? 'unit',
    salesPrice: item ? String(Number(item.salesPrice)) : '0',
    purchasePrice: item ? String(Number(item.purchasePrice)) : '0',
    salesAccountId: item?.salesAccountId ?? '',
    expenseAccountId: item?.expenseAccountId ?? '',
    salesTaxCode: item?.salesTaxCode ?? '',
    purchaseTaxCode: item?.purchaseTaxCode ?? '',
    ewtAtc: item?.ewtAtc ?? '',
    active: item?.active ?? true,
  });
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<typeof form>): void => setForm((f) => ({ ...f, ...patch }));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...form,
        salesAccountId: form.salesAccountId || null,
        expenseAccountId: form.expenseAccountId || null,
        salesTaxCode: form.salesTaxCode || null,
        purchaseTaxCode: form.purchaseTaxCode || null,
        ewtAtc: form.ewtAtc || null,
        barcode: form.type === 'INVENTORY' ? form.barcode || null : null,
        reorderPoint: form.type === 'INVENTORY' && form.reorderPoint ? form.reorderPoint : null,
      };
      return item ? api(`/items/${item.id}`, { method: 'PATCH', body }) : api('/items', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Could not save.'),
  });
  return (
    <Modal title={item ? `Edit ${item.name}` : 'New item'} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          <Field label="Code" required><input className={inputClass} value={form.code} onChange={(e) => set({ code: e.target.value })} /></Field>
          <Field label="Name" required><input className={inputClass} value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Type">
            <select className={inputClass} value={form.type} onChange={(e) => set({ type: e.target.value })}>
              <option value="SERVICE">Service</option>
              <option value="NON_INVENTORY">Non-inventory goods</option>
              <option value="INVENTORY">Inventory (tracked stock)</option>
            </select>
          </Field>
          <Field label="Unit"><input className={inputClass} value={form.uom} onChange={(e) => set({ uom: e.target.value })} /></Field>
          <Field label="Sales price"><input className={`${inputClass} text-right`} inputMode="decimal" value={form.salesPrice} onChange={(e) => set({ salesPrice: e.target.value })} /></Field>
          <Field label="Cost"><input className={`${inputClass} text-right`} inputMode="decimal" value={form.purchasePrice} onChange={(e) => set({ purchasePrice: e.target.value })} /></Field>
          <Field label="Sales tax">
            <select className={inputClass} value={form.salesTaxCode} onChange={(e) => set({ salesTaxCode: e.target.value })}>
              <option value="">Default</option>
              {(taxCodes.data ?? []).map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="EWT when bought">
            <select className={inputClass} value={form.ewtAtc} onChange={(e) => set({ ewtAtc: e.target.value })}>
              <option value="">From supplier</option>
              {(atcs.data ?? []).filter((a) => a.active).map((a) => <option key={a.code} value={a.code}>{a.code}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Revenue account">
            <AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'INCOME')} value={form.salesAccountId} onChange={(id) => set({ salesAccountId: id })} placeholder="Default revenue" />
          </Field>
          <Field label="Expense account">
            <AccountSelect accounts={(accounts.data ?? []).filter((a) => a.type === 'EXPENSE' || a.type === 'ASSET')} value={form.expenseAccountId} onChange={(id) => set({ expenseAccountId: id })} placeholder="From supplier" />
          </Field>
        </div>
        {form.type === 'INVENTORY' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Barcode"><input className={inputClass} value={form.barcode} onChange={(e) => set({ barcode: e.target.value })} /></Field>
            <Field label="Reorder point" hint="Alert when stock falls to this quantity."><input className={inputClass} inputMode="decimal" value={form.reorderPoint} onChange={(e) => set({ reorderPoint: e.target.value })} /></Field>
            <p className="text-xs text-slate-500 sm:col-span-2">Bills put this item into stock at cost; invoices take it out at weighted-average cost with the cost of sales posted automatically.</p>
          </div>
        )}
        {item && (
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4" checked={form.active} onChange={(e) => set({ active: e.target.checked })} /> Active</label>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
